import { Component } from '@theme/component';

/**
 * @typedef {Object} LabiargeConsultationFormRefs
 * @property {HTMLFormElement} [form] - The contact form
 * @property {HTMLInputElement} [phoneInput] - Phone number input
 * @property {HTMLSelectElement} [countryCodeSelect] - Country dial-code select
 * @property {HTMLInputElement} [dateStart] - Preferred start date
 * @property {HTMLInputElement} [dateEnd] - Preferred end date
 * @property {HTMLHeadingElement} [successHeading] - Post-submit success h3 (tabindex=-1)
 * @property {HTMLHeadingElement} [errorHeading] - Post-submit error h3 (tabindex=-1)
 */

/**
 * Consultation form client-side enhancement. Three responsibilities:
 *
 * 1. Post-render focus management. Shopify's {% form 'contact' %} redirects
 *    back to the same page with form.posted_successfully? or form.errors
 *    populated. The block renders a success or error heading with
 *    tabindex="-1"; on hydration we pull keyboard focus to that heading so
 *    screen-reader + keyboard users are informed of the submission outcome
 *    (form-accessibility.mdc:81–88).
 *
 * 2. Client-side date-range validation. Native <input type="date"> doesn't
 *    cross-validate start vs end. If end < start we set a custom validity
 *    message sourced from the labiarge.consultation.error_invalid_date_range
 *    locale key (injected server-side into a data attribute).
 *
 * 3. Country-code concatenation. The form has two separate inputs:
 *    contact[country_code] and contact[phone]. Just before submission we
 *    prepend the code to the phone value so Shopify's contact email shows
 *    a single canonical phone field. If JS fails to load, both raw values
 *    are still emailed. No information is lost, only formatting differs.
 *
 * @extends {Component<LabiargeConsultationFormRefs>}
 */
class LabiargeConsultationFormComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    // #1 Focus management on submit-redirect render.
    if (this.refs.successHeading instanceof HTMLElement) {
      this.refs.successHeading.focus({ preventScroll: false });
    } else if (this.refs.errorHeading instanceof HTMLElement) {
      this.refs.errorHeading.focus({ preventScroll: false });
    }

    // #2 Wire date inputs for range validation.
    const { dateStart, dateEnd } = this.refs;
    if (dateStart && dateEnd) {
      dateStart.addEventListener('change', () => this.#validateDateRange());
      dateEnd.addEventListener('change', () => this.#validateDateRange());
      dateEnd.addEventListener('input', () => dateEnd.setCustomValidity(''));
    }

    // #3 Attach submit listener directly to the inner <form>. The Shopify
    // {% form 'contact' %} Liquid tag doesn't accept arbitrary HTML attrs,
    // so we can't use Horizon's on:submit="/handleSubmit" delegation. Walk
    // the DOM to find it.
    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('submit', (event) => this.handleSubmit(event));
    }
  }

  /**
   * Delegated from the form's `on:submit="/handleSubmit"` attribute.
   * Concatenates country code into phone, runs the date-range check.
   *
   * @param {SubmitEvent} event
   */
  handleSubmit(event) {
    if (!this.#validateDateRange()) {
      event.preventDefault();
      this.refs.dateEnd?.reportValidity();
      return;
    }

    const { phoneInput, countryCodeSelect } = this.refs;
    if (phoneInput && countryCodeSelect && countryCodeSelect.value) {
      // Only prepend once per submission; if the user has already edited the
      // phone to include the dial code themselves, don't duplicate it.
      if (!phoneInput.value.startsWith(countryCodeSelect.value)) {
        phoneInput.value = `${countryCodeSelect.value} ${phoneInput.value}`.trim();
      }
    }
  }

  /** @returns {boolean} */
  #validateDateRange() {
    const { dateStart, dateEnd } = this.refs;
    if (!dateStart || !dateEnd) return true;
    if (!dateStart.value || !dateEnd.value) return true;

    if (dateEnd.value < dateStart.value) {
      const message =
        this.dataset.errorInvalidDateRange ||
        'End date must be on or after start date';
      dateEnd.setCustomValidity(message);
      return false;
    }

    dateEnd.setCustomValidity('');
    return true;
  }
}

customElements.define('labiarge-consultation-form-component', LabiargeConsultationFormComponent);
